import React, { useCallback, useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import { withStyles } from '@material-ui/core/styles';
import { connect } from 'react-redux';
import { getTranslate } from 'react-localize-redux';
import { bindActionCreators } from 'redux';
import classnames from 'classnames';

import {
    Typography, TextField, Button, IconButton,
} from '@material-ui/core';

import * as PreviewStore from '../../app/store/PreviewStore';
import { timestamp } from '../../youwell-common/dateUtils';
import { getElementTitle } from '../../youwell-common/contentUtils';
import { ClearIcon } from '../icons';
import useForm from '../../youwell-common/useForm';
import { validateRequired } from '../../youwell-common/validationUtils';
import { contentElemenIdPrefix } from '../../youwell-common/constants';

const styles = theme => ({
    root: {
        background: theme.palette.common.white,
        borderLeft: theme.youwell.borders.dim,
        padding: theme.spacing(2),

        [theme.breakpoints.down('xs')]: {
            padding: theme.spacing(1),
        },
    },
    description: {
        marginTop: theme.spacing(8),

        [theme.breakpoints.down('xs')]: {
            display: 'none', // not possible to select element on small screen so description is not relevant..
        },
    },
    commentInputFields: {
        margin: theme.spacing(2, 0),
        padding: theme.spacing(2),
        background: theme.palette.grey[50],
        display: 'flex',
        flexDirection: 'column',

        [theme.breakpoints.down('xs')]: {
            padding: theme.spacing(1),
        },
    },
    commentElement: {
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        borderBottom: theme.youwell.borders.primary,
    },
    commentInput: {
        marginTop: theme.spacing(1),
    },
    sendButton: {
        marginTop: theme.spacing(1),
        alignSelf: 'flex-end',
    },
    comment: {
        marginBottom: theme.spacing(2),
    },
    commentHead: {
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        borderBottom: theme.youwell.borders.dim,
    },
    lineWrapper: {
        position: 'fixed',
        top: 0,
        left: 0,
        height: '100vh',
        width: '100vw',
        pointerEvents: 'none',
        color: theme.palette.primary.main,
    },
});


const calculate = (from, to) => {
    const fromEl = document.getElementById(from);
    const toEl = document.getElementById(to);

    if (!fromEl || !toEl) {
        return '';
    }

    const fromPosRect = fromEl.getBoundingClientRect();
    const toPosRect = toEl.getBoundingClientRect();

    const fromPos = {
        x: fromPosRect.left - 8,
        y: fromPosRect.top + fromEl.offsetHeight / 2,
    };
    const toPos = {
        x: toPosRect.right + 8,
        y: toPosRect.top + toEl.offsetHeight / 2,
    };

    return `M${fromPos.x},${fromPos.y} `
+ `C${fromPos.x - 80},${fromPos.y} ${toPos.x + 80},${toPos.y} ${
    toPos.x},${toPos.y}`;
};

const Line = ({
    from, to, classes,
}) => {
    const lineEl = useRef(null);


    const update = useCallback(() => {
        if (lineEl.current) {
            lineEl.current.setAttribute('d', calculate(from, to));
        }
    }, [from, to]);

    useEffect(() => {
        update();
    }, [update]);

    useEffect(() => {
        window.addEventListener('scroll', update);

        return () => {
            window.removeEventListener('scroll', update);
        };
    }, [update]);

    return (
        <div className={classes.lineWrapper}>
            <svg xmlns="http://www.w3.org/2000/svg" width="100%" height="100%">
                <defs>
                    <marker
                        id="arrowhead"
                        viewBox="0 0 10 10"
                        refX="3"
                        refY="5"
                        markerWidth="6"
                        markerHeight="6"
                        orient="auto"
                        fill="currentColor"
                    >
                        <path d="M 0 0 L 10 5 L 0 10 z" />
                    </marker>
                </defs>
                <g fill="none" stroke="currentColor" strokeWidth="2" markerEnd="url(#arrowhead)">
                    <path id="arrowLeft" ref={lineEl} d={calculate(from, to)} />
                </g>
            </svg>
        </div>
    );
};

Line.propTypes = {
    from: PropTypes.string.isRequired,
    to: PropTypes.string.isRequired,
    classes: PropTypes.object.isRequired,
};

const ContentCommenting = ({
    classes, className, translate, task, commentElementId, saveTaskComment, clearCommentElement, getTaskComments, comments,
}) => {
    const send = ({ formValues }) => {
        saveTaskComment({
            taskId: task.id, elementId: commentElementId, comment: formValues.comment, name: formValues.name,
        }, {
            // callback: () => setValues({ name: formValues.name }),
        });
    };

    const {
        formValues,
        validationErrors,
        handleChange,
        handleSubmit,
    } = useForm(send, {
        name: validateRequired,
        comment: validateRequired,
    }, {});

    useEffect(
        () => getTaskComments(task.id, { reducerParams: { taskId: task.id } }),
        [getTaskComments, task.id],
    );

    const elementTitles = task.contentElements?.reduce((prev, curr) => (
        curr ? { ...prev, [curr.id]: getElementTitle(curr) } : prev
    ), {});

    return (
        <div className={classnames(classes.root, className)}>
            <Typography className={classes.description}>
                {translate('taskCommenting.description')}
            </Typography>

            <form onSubmit={handleSubmit}>
                <div className={classes.commentInputFields}>
                    {commentElementId && (
                        <div className={classes.commentElement} id="commentElement">
                            <Line from="commentElement" to={`${contentElemenIdPrefix}${commentElementId}`} classes={classes} />
                            <Typography variant="caption" gutterBottom>
                                {`${translate('taskCommenting.element')}: ${elementTitles[commentElementId]}`}
                            </Typography>
                            <IconButton size="small" onClick={clearCommentElement}><ClearIcon /></IconButton>
                        </div>
                    )}
                    <TextField
                        label={translate('taskCommenting.name')}
                        fullWidth
                        value={formValues.name || ''}
                        onChange={handleChange('name')}
                        error={!!validationErrors.name}
                        helperText={validationErrors.name ? translate(validationErrors.name) : null}
                    />
                    <TextField
                        className={classes.commentInput}
                        label={translate('taskCommenting.newComment')}
                        multiline
                        minRows={10}
                        fullWidth
                        value={formValues.comment || ''}
                        onChange={handleChange('comment')}
                        error={!!validationErrors.comment}
                        helperText={validationErrors.comment ? translate(validationErrors.comment) : null}
                    />
                    <Button type="submit" className={classes.sendButton} color="primary" variant="outlined">
                        {translate('taskCommenting.send')}
                    </Button>
                </div>
            </form>

            {comments?.map(c => (
                <div key={c.id} className={classes.comment}>
                    <div className={classes.commentHead}>
                        <Typography variant="caption">
                            {timestamp(c.created)}
                        </Typography>
                        {c.elementId && (
                            <Typography variant="caption">
                                {elementTitles[c.elementId]}
                            </Typography>
                        )}
                    </div>
                    <Typography>
                        {c.comment}
                    </Typography>
                    <Typography variant="caption" align="right" display="block">
                        {c.name}
                    </Typography>
                </div>
            ))}
        </div>
    );
};

ContentCommenting.propTypes = {
    classes: PropTypes.object.isRequired,
    className: PropTypes.string,
    translate: PropTypes.func.isRequired,
    task: PropTypes.object.isRequired,
    comments: PropTypes.array,
    saveTaskComment: PropTypes.func.isRequired,
    getTaskComments: PropTypes.func.isRequired,
    clearCommentElement: PropTypes.func.isRequired,
    commentElementId: PropTypes.number,
};

ContentCommenting.defaultProps = {
    className: null,
    commentElementId: null,
    comments: null,
};

const mapStateToProps = (state, ownProps) => ({
    translate: getTranslate(state.localize),
    comments: state.previewStore.comments[ownProps.task.id],
    commentElementId: state.previewStore.commentElementId,
});

const mapDispatchToProps = dispatch => ({
    ...bindActionCreators(PreviewStore.actionCreators, dispatch),
});

export default connect(mapStateToProps, mapDispatchToProps)(withStyles(styles)(ContentCommenting));
